import { useState, useEffect, lazy, Suspense } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Header } from './components/Header';
import { Home } from './components/Home';
import { Footer } from './components/Footer';
import { LoadingSpinner } from './components/LoadingSpinner';

// Lazy load pages that are not immediately visible
const About = lazy(() => import('./components/About').then(module => ({ default: module.About })));
const Projects = lazy(() => import('./components/Projects').then(module => ({ default: module.Projects })));
const Contact = lazy(() => import('./components/Contact').then(module => ({ default: module.Contact })));
const PrivacyPolicyMindfork = lazy(() => import('./components/PrivacyPolicyMindfork').then(module => ({ default: module.PrivacyPolicyMindfork })));
const MindFork = lazy(() => import('./components/MindFork').then(module => ({ default: module.MindFork })));
const Technology = lazy(() => import('./components/Technology').then(module => ({ default: module.Technology })));
const VANTA = lazy(() => import('./components/VANTA').then(module => ({ default: module.VANTA })));
const Glow = lazy(() => import('./components/Glow').then(module => ({ default: module.Glow })));

/**
 * Represents the available page routes in the Cordalux application.
 * 
 * @public
 * @category Types
 * @since 1.0.0
 */
export type Page = 'home' | 'about' | 'projects' | 'contact' | 'privacy' | 'mindfork' | 'technology' | 'vanta' | 'glow';

/**
 * Main application component for the Cordalux Meta-Orchestrator Hub.
 * 
 * The App component serves as the root of the React application, managing global state
 * for navigation and theme preferences. It provides a single-page application experience
 * with smooth animations between different sections.
 * 
 * @returns The complete Cordalux application interface
 * 
 * @public
 * @category Components
 * @since 1.0.0
 * 
 * @example
 * ```tsx
 * import App from './App';
 * import { createRoot } from 'react-dom/client';
 * 
 * const root = createRoot(document.getElementById('root')!);
 * root.render(<App />);
 * ```
 * 
 * @remarks
 * Features include:
 * - Single-page navigation with smooth transitions
 * - Dark/light mode toggle with system persistence
 * - Responsive design for all device sizes
 * - Framer Motion animations for enhanced UX
 */
function App() {
  /** Current active page in the navigation */
  const [currentPage, setCurrentPage] = useState<Page>('home');
  
  /** Global dark mode state */
  const [isDarkMode, setIsDarkMode] = useState(true);

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  useEffect(() => {
    const validPages: Page[] = ['home', 'about', 'projects', 'contact', 'privacy', 'mindfork', 'technology', 'vanta', 'glow'];

    const syncFromHash = () => {
      const hash = window.location.hash.replace('#', '') as Page | '';
      if (hash && validPages.includes(hash)) {
        setCurrentPage(hash);
      } else if (!hash) {
        setCurrentPage('home');
      }
    };

    syncFromHash();
    window.addEventListener('hashchange', syncFromHash);
    return () => window.removeEventListener('hashchange', syncFromHash);
  }, []);

  useEffect(() => {
    const hash = currentPage === 'home' ? '' : `#${currentPage}`;
    const newUrl = hash ? `${window.location.pathname}${window.location.search}${hash}` : `${window.location.pathname}${window.location.search}`;
    if (window.location.hash !== hash) {
      window.history.replaceState(null, '', newUrl);
    }
  }, [currentPage]);

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
  };

  const renderPage = () => {
    switch (currentPage) {
      case 'home':
        return <Home />;
      case 'about':
        return <About />;
      case 'projects':
        return <Projects />;
      case 'contact':
        return <Contact />;
      case 'privacy':
        return <PrivacyPolicyMindfork />;
      case 'mindfork':
        return <MindFork />;
      case 'technology':
        return <Technology />;
      case 'vanta':
        return <VANTA />;
      case 'glow':
        return <Glow />;
      default:
        return <Home />;
    }
  };

  return (
    <div className={`min-h-screen transition-colors duration-500 ${
      isDarkMode 
        ? 'bg-black text-white' 
        : 'bg-white text-black'
    }`}>
      <Header 
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        isDarkMode={isDarkMode}
        toggleDarkMode={toggleDarkMode}
      />
      
      <AnimatePresence mode="wait">
        <motion.main
          key={currentPage}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
        >
          <Suspense fallback={<LoadingSpinner />}>
            {renderPage()}
          </Suspense>
        </motion.main>
      </AnimatePresence>

      <Footer />
    </div>
  );
}

export default App;
